// src/features/defects/api/useDefects.ts
// Defect CRUD + Comments + Relationships + Stats
// All endpoints verified against openapi.json (defect-service)
// Core CRUD paths via API_CONFIG.ENDPOINTS.DEFECTS
// Sub-resource paths (comments, relationships) constructed dynamically per OpenAPI
// © 2025–2026 MCX Services, LLC. All rights reserved.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import { API_CONFIG } from '@/lib/config/api.config'
import type {
  Defect,
  DefectWithDetails,
  DefectCreate,
  DefectUpdate,
  DefectsListResponse,
  DefectStats,
  DefectFilters,
  CommentCreate,
  DefectRelationshipCreate,
} from '@/types/defect.types'

// ============================================================================
// Query Keys
// ============================================================================

export const defectKeys = {
  all: ['defects'] as const,
  lists: () => [...defectKeys.all, 'list'] as const,
  list: (filters: DefectFilters) => [...defectKeys.lists(), filters] as const,
  details: () => [...defectKeys.all, 'detail'] as const,
  detail: (id: number) => [...defectKeys.details(), id] as const,
  stats: (projectId?: number) => [...defectKeys.all, 'stats', projectId] as const,
}

// ============================================================================
// Defects List & Details
// ============================================================================

export const useDefects = (filters: DefectFilters = {}) => {
  return useQuery({
    queryKey: defectKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams()

      if (filters.project_id) params.append('project_id', filters.project_id.toString())
      if (filters.status) params.append('status', filters.status)
      if (filters.severity) params.append('severity', filters.severity)
      if (filters.priority) params.append('priority', filters.priority)
      if (filters.assigned_to) params.append('assigned_to', filters.assigned_to.toString())
      if (filters.reported_by) params.append('reported_by', filters.reported_by.toString())
      if (filters.testcase_id) params.append('testcase_id', filters.testcase_id.toString())
      if (filters.testplan_id) params.append('testplan_id', filters.testplan_id.toString())
      if (filters.execution_id) params.append('execution_id', filters.execution_id.toString())
      if (filters.search) params.append('search', filters.search)
      if (filters.tags) params.append('tags', filters.tags)
      if (filters.limit) params.append('limit', filters.limit.toString())
      if (filters.offset) params.append('offset', filters.offset.toString())

      const query = params.toString()
      const { data } = await apiClient.get<DefectsListResponse>(
        `${API_CONFIG.ENDPOINTS.DEFECTS.LIST}${query ? `?${query}` : ''}`
      )
      return data
    },
  })
}

export const useDefect = (defectId: number) => {
  return useQuery({
    queryKey: defectKeys.detail(defectId),
    queryFn: async () => {
      const { data } = await apiClient.get<DefectWithDetails>(
        API_CONFIG.ENDPOINTS.DEFECTS.GET(defectId.toString())
      )
      return data
    },
    enabled: !!defectId,
  })
}

export const useDefectStats = (projectId?: number) => {
  return useQuery({
    queryKey: defectKeys.stats(projectId),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (projectId) params.append('project_id', projectId.toString())

      const query = params.toString()
      const { data } = await apiClient.get<DefectStats>(
        `/api/defects/stats/summary${query ? `?${query}` : ''}`
      )
      return data
    },
  })
}

// ============================================================================
// Mutations
// ============================================================================

export const useCreateDefect = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (defect: DefectCreate) => {
      const { data } = await apiClient.post<Defect>(
        API_CONFIG.ENDPOINTS.DEFECTS.CREATE,
        defect
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: defectKeys.lists() })
      queryClient.invalidateQueries({ queryKey: defectKeys.stats() })
    },
  })
}

export const useUpdateDefect = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: DefectUpdate }) => {
      const response = await apiClient.put<Defect>(
        API_CONFIG.ENDPOINTS.DEFECTS.UPDATE(id.toString()),
        data
      )
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: defectKeys.detail(variables.id) })
      queryClient.invalidateQueries({ queryKey: defectKeys.lists() })
      queryClient.invalidateQueries({ queryKey: defectKeys.stats() })
    },
  })
}

export const useDeleteDefect = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(API_CONFIG.ENDPOINTS.DEFECTS.DELETE(id.toString()))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: defectKeys.lists() })
      queryClient.invalidateQueries({ queryKey: defectKeys.stats() })
    },
  })
}

// ============================================================================
// Comments
// OpenAPI: POST /api/defects/{defect_id}/comments
// Body: { comment (req), is_internal? (bool), attachments? (array) }
// Response: 201
// ============================================================================

export const useAddComment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ defectId, comment }: { defectId: number; comment: CommentCreate }) => {
      const { data } = await apiClient.post(
        `/api/defects/${defectId}/comments`,
        comment
      )
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: defectKeys.detail(variables.defectId) })
    },
  })
}

// ============================================================================
// Relationships
// OpenAPI: POST /api/defects/{defect_id}/relationships
// Body: { related_defect_id (req), relationship_type (req) }
// Response: 201
// ============================================================================

export const useAddRelationship = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      defectId,
      relationship,
    }: {
      defectId: number
      relationship: DefectRelationshipCreate
    }) => {
      const { data } = await apiClient.post(
        `/api/defects/${defectId}/relationships`,
        relationship
      )
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: defectKeys.detail(variables.defectId) })
    },
  })
}
